import { AnimatePresence, motion } from "framer-motion";
import { X, Heart, Trash2, ShoppingBag, Send } from "lucide-react";
import { useWishlist } from "../context/WishlistContext";
import { useCart } from "../context/CartContext";
import { money, scrollToId } from "../lib/format";

export default function WishlistDrawer({ settings }) {
  const { items, open, setOpen, remove, count } = useWishlist();
  const { addItem } = useCart();
  const currency = settings?.currency || "RD$";

  const share = () => {
    if (!settings?.whatsapp_number) return;
    let msg = `🌸 *Mi lista de deseos — MO by Vir*\n\n`;
    items.forEach((i) => {
      msg += `• ${i.name} — ${money(i.price, currency)}\n`;
    });
    msg += `\n¿Me ayudas con estas piezas? 💕`;
    window.open(
      `https://wa.me/${settings?.whatsapp_number}?text=${encodeURIComponent(msg)}`,
      "_blank"
    );
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-[70] bg-espresso/40 backdrop-blur-sm"
          />
          <motion.aside
            data-testid="wishlist-drawer"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", ease: [0.22, 1, 0.36, 1], duration: 0.5 }}
            className="fixed top-0 right-0 z-[80] h-full w-full sm:w-[420px] bg-cream flex flex-col"
          >
            <div className="flex items-center justify-between px-6 py-5 border-b border-line">
              <span className="font-serif text-2xl flex items-center gap-2">
                <Heart size={20} className="text-rose" /> Lista de deseos
              </span>
              <button
                data-testid="wishlist-close"
                onClick={() => setOpen(false)}
                className="text-espresso hover:text-rose transition-colors"
              >
                <X size={22} />
              </button>
            </div>

            {count === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center text-center px-8 gap-4">
                <Heart size={40} strokeWidth={1} className="text-taupe" />
                <p className="font-serif text-xl">Aún no tienes favoritos</p>
                <p className="text-taupe text-sm font-light">
                  Toca el corazón en cualquier pieza para guardarla aquí.
                </p>
                <button
                  data-testid="wishlist-empty-shop"
                  onClick={() => {
                    setOpen(false);
                    scrollToId("catalogo");
                  }}
                  className="mt-2 bg-espresso text-cream rounded-full px-7 py-3 text-xs uppercase tracking-[0.2em] hover:bg-rose transition-colors"
                >
                  Ver catálogo
                </button>
              </div>
            ) : (
              <>
                <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
                  {items.map((i) => (
                    <div key={i.id} data-testid={`wishlist-item-${i.id}`} className="flex gap-4">
                      <div className="w-20 h-24 bg-surface overflow-hidden shrink-0">
                        <img src={i.image} alt={i.name} className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1 flex flex-col justify-between py-1">
                        <div className="flex justify-between gap-2">
                          <h4 className="font-serif text-lg leading-tight">{i.name}</h4>
                          <button
                            data-testid={`wishlist-remove-${i.id}`}
                            onClick={() => remove(i.id)}
                            className="text-taupe hover:text-rose transition-colors"
                          >
                            <Trash2 size={15} />
                          </button>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium">{money(i.price, currency)}</span>
                          <button
                            data-testid={`wishlist-add-cart-${i.id}`}
                            onClick={() => addItem(i)}
                            className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] text-espresso border-b border-espresso pb-0.5 hover:text-rose hover:border-rose transition-colors"
                          >
                            <ShoppingBag size={13} /> Agregar
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="border-t border-line px-6 py-5">
                  <button
                    data-testid="wishlist-share-btn"
                    onClick={share}
                    className="w-full bg-rose text-cream rounded-full py-4 text-xs uppercase tracking-[0.2em] hover:bg-rose-hover transition-colors flex items-center justify-center gap-2"
                  >
                    <Send size={15} /> Compartir por WhatsApp
                  </button>
                </div>
              </>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
